import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export interface UserInfo {
  id: string;
  name: string;
  sex?: string;
  email?: string;
  photo?: string;
  technical?: boolean;
  typeCredential?: string;
}

export const USER_STORAGE_KEY = '__pwa_user_me';
const STORAGE_KEY = USER_STORAGE_KEY;

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private get userAccountsUrl(): string {
    const hostname = window.location.hostname;
    const base = (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('test.betha.cloud'))
      ? 'https://plataforma-autorizacoes.test.betha.cloud'
      : 'https://plataforma-autorizacoes.betha.cloud';
    return `${base}/user-accounts/v0.1`;
  }

  private _userInfo$ = new BehaviorSubject<UserInfo | null>(this.loadFromStorage());

  /** Observable com as informações do usuário logado */
  get userInfo$(): Observable<UserInfo | null> {
    return this._userInfo$.asObservable();
  }

  /** Valor síncrono atual */
  get userInfo(): UserInfo | null {
    return this._userInfo$.value;
  }

  constructor(private http: HttpClient) {}

  /**
   * Busca os dados do usuário autenticado via /user-accounts/v0.1/api/users/@me.
   * O token Bearer é adicionado automaticamente pelo AuthInterceptorService.
   * Salva o resultado no localStorage para acesso offline/imediato.
   */
  fetchCurrentUser(): Observable<UserInfo | null> {
    return this.http.get<UserInfo>(`${this.userAccountsUrl}/api/users/@me`).pipe(
      tap(user => {
        this.saveToStorage(user);
        this._userInfo$.next(user);
      }),
      catchError(err => {
        return of(null);
      })
    );
  }

  /** Retorna o nome de exibição do usuário logado */
  getNome(): string {
    return this.userInfo?.name || '';
  }

  /** Retorna o login/id do usuário logado */
  getUsername(): string {
    return this.userInfo?.id || '';
  }

  /**
   * Constrói a URL da foto do usuário autenticado.
   * O endpoint requer o token de acesso como query param, igual ao AngularJS:
   * {userAccountsUrl}/api/users/{userId}/photo?access_token={token}
   */
  getUserPhotoUrl(token: string): string | null {
    const userId = this.userInfo?.id;
    if (!userId || !token) {
      return this.userInfo?.photo || null;
    }
    return `${this.userAccountsUrl}/api/users/${userId}/photo?access_token=${encodeURIComponent(token)}`;
  }

  /** Limpa os dados do usuário (logout) */
  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    this._userInfo$.next(null);
  }

  private saveToStorage(user: UserInfo): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } catch {
      // Ignorar erros de storage
    }
  }

  private loadFromStorage(): UserInfo | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw) as UserInfo;
      }
    } catch {
      // Ignorar erros de parse
    }
    return null;
  }
}
